import { useT } from '@disa/i18n';
import { CircleQuestionMark, Settings, ShieldCheck } from 'lucide-react';
import { useState } from 'react';
import { adminUrl, readAdminHint } from '@/core/admin-hint';
import { DOCK_SECTIONS, type ShellView } from '../helpers/views';
import { useDockMagnify } from '../hooks/use-dock-magnify';
import { DockEntry } from './DockEntry';

interface Props {
  view: ShellView;
  onNavigate: () => void;
  onSettingsOpen: () => void;
  onHelpOpen: () => void;
}

function pathFor(view: ShellView) {
  switch (view) {
    case 'home':
      return '/';
    case 'library':
      return '/library';
    case 'tools':
      return '/tools';
    case 'lineups':
      return '/lineups';
    case 'tactics':
      return '/tactics';
    case 'stats':
      return '/stats';
  }
}

export function ShellDock({ view, onNavigate, onSettingsOpen, onHelpOpen }: Props) {
  const t = useT();
  const panelRef = useDockMagnify();
  // Read once: the hint changes only by signing in to the admin, which is another page.
  const [isAdmin] = useState(readAdminHint);

  return (
    // The band takes no pointer events, so the field and the content under it stay reachable
    // everywhere the panel itself is not.
    <div className="pointer-events-none fixed inset-x-0 bottom-0 z-30 flex justify-center px-3 pb-3">
      <div ref={panelRef} className="atlas-dock pointer-events-auto flex items-end">
        <nav aria-label={t('library.shell.nav')}>
          <ul className="flex list-none items-end atlas-dock-group p-0">
            {DOCK_SECTIONS.map((section) => (
              <li key={section.view}>
                <DockEntry
                  icon={section.icon}
                  labelPath={section.labelPath}
                  isCurrent={section.view === view}
                  isSoon={section.isSoon}
                  tone={section.tone}
                  to={pathFor(section.view)}
                  onSelect={onNavigate}
                />
              </li>
            ))}
          </ul>
        </nav>

        {/* The same two sheets settings and help own, so the way in keeps no copy of either. */}
        <div className="atlas-dock-group flex items-end">
          {isAdmin && (
            <DockEntry
              icon={ShieldCheck}
              labelPath="library.shell.admin"
              tone="linear-gradient(160deg, #4b5565, #1f2531)"
              href={adminUrl()}
            />
          )}

          <DockEntry
            icon={Settings}
            labelPath="common.settings"
            tone="linear-gradient(160deg, #8a919e, #505966)"
            onSelect={onSettingsOpen}
          />

          <DockEntry
            icon={CircleQuestionMark}
            labelPath="common.help"
            tone="linear-gradient(160deg, #54b9d5, #217c9c)"
            onSelect={onHelpOpen}
          />
        </div>
      </div>
    </div>
  );
}
